import { action, autorun, computed, makeObservable, observable, reaction } from 'mobx';

import type { CleanGeoData, RawGeoData } from '../../features/geography/types';
import { analytics } from '../../services/analytics';
import { getRequest } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import { singleFlight } from '../../utils/singleFlight';

export class LiveGeoStore {
  constructor() {
    makeObservable(this);

    autorun(() => this.getCurrentGeoData());

    reaction(
      () => this.countryCode,
      (countryCode) => {
        if (countryCode) {
          analytics.setCommonProps({ country_code: countryCode });
        }
      },
    );
  }

  public fetchCurrentGeo = async () => {
    try {
      const res = await getRequest<{
        data: {
          clean: CleanGeoData | null;
          raw: RawGeoData | null;
        };
      }>('geography/current-geo');

      this.setCurrentGeo(res.data.clean, res.data.raw);

      return res.data;
    } catch (geoError) {
      const errorMessage = getErrorMessage(geoError);
      console.error(`Error fetching current geo: ${errorMessage}`);
      return null;
    }
  };

  public getCurrentGeoData = singleFlight(this.fetchCurrentGeo);

  @observable
  public currentGeo: CleanGeoData | null = null;

  @observable
  public rawGeo: RawGeoData | null = null;

  @computed
  public get countryCode() {
    return this.currentGeo?.countryCode ?? this.rawGeo?.countryCode;
  }

  @computed
  public get isEurope() {
    return Boolean(this.countryCode && ['DE', 'AT'].includes(this.countryCode));
  }

  @action
  private setCurrentGeo(geo: CleanGeoData | null, raw: RawGeoData | null) {
    this.currentGeo = geo;
    this.rawGeo = raw;
  }
}
export const liveGeoStore = new LiveGeoStore();
